import React, { useState, useEffect, useCallback } from 'react';
import ReactTags from 'react-tag-autocomplete';
import _ from 'lodash';

import useDebounce from 'helpers/useDebounce';
import api from 'services';
import { RaisedButton } from 'material-ui';
import { addNotification as _addNotification } from 'actions/notifications/notifications';
import { updateTagsDataInRow } from 'actions/cargoes/realNames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as immer from 'immer';
import './styles.scss';

const referenceTags = [
    'BAUXITE',
    'COAL',
    'COKE',
    'FERTILIZERS',
    'GRAINS',
    'ORE',
    'OTHERS',
    'PETCOKE',
    'SALT',
    'STEELS',
    'SUGAR',
];
const tagMatchMap = _.fromPairs(referenceTags.map(t => [t.replace(/e?s$/i, ''), t]));
const recommendMatcher = new RegExp(referenceTags.map(t => `\\b${t.replace(/e?s$/i, '')}\\b`).join('|'), 'ig');

const RoroTAG = 'RORO';
const ProjectsTAG = 'PROJECTS';

const tagsByDetails = {
    1: [ProjectsTAG],
    11: [RoroTAG],
    12: [ProjectsTAG],
    13: [ProjectsTAG],
};

function findRecommendedTags(name, cargoDetails) {
    const matches = name.matchAll(recommendMatcher);

    const tags = Array.from(matches, m => ({ value: tagMatchMap[m[0].toUpperCase()], categoryName: 'cargo' }));

    tagsByDetails[cargoDetails] && tagsByDetails[cargoDetails].forEach((tagName) => {
        tags.push({ value: tagName.toUpperCase(), categoryName: 'cargo' });
    });

    return tags;
}

function initializeInitialTags(initialTags, cargoName, cargoDetails) {
    const tags = [];

    initialTags.forEach((tag) => {
        tags.push({ ...tag, saved: true });
    });

    if (!tags.length) {
        const recommendTags = findRecommendedTags(cargoName || '', cargoDetails);
    
        recommendTags.forEach((tag) => {
            tags.push(tag);
        });
    }

    return _.uniqBy(tags, 'value');
}

function Tags({cargoId, cargoName, cargoDetails, addNotification, initialTags, updateTagsDataInRow}) {
    const _pureTags = initializeInitialTags(initialTags, cargoName, cargoDetails);

    const [tags, setTags] = useState(_pureTags);
    const [pureTags, setPureTags] = useState(_pureTags);
    const [suggestions, setSuggestions] = useState([]);
    const [searchTag, setSearchTag] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isChanged, setIsChanged] = useState(tags.some(t => !t.saved));

    useEffect(() => {
        initialTags.forEach((tag) => {
            handleAddition({ ...tag, saved: true });
        });

        if (!initialTags.length) {
            const _tags = findRecommendedTags(cargoName || '', cargoDetails);

            _tags.forEach((tag) => {
                handleAddition(tag);
            });
        }
    }, [initialTags]);

    const handleDelete = index => {
        if (index < 0) return;
        
        let tagsCopy = _.cloneDeep(tags);
        const removed = tagsCopy.splice(index, 1);
        setTags(tagsCopy);
        
        setIsChanged(shouldRemovedSetChanged(removed[0], tagsCopy));
    };

    const shouldRemovedSetChanged = (removed, newTags) => {
        if (!removed) return false;

        if (isChanged && 
            (
                pureTags.find(r => r._id === removed._id) || 
                newTags.find(t => !t.saved) ||
                (pureTags.length && !pureTags.some(t => newTags.find(n => n._id === t._id)))
            )
        ) return true;

        return removed.saved;
    }

    const handleAddition = (tag) => {
        const newTags = immer.produce(tags, (draftTags) => {
            if (!draftTags.some(dt => dt.value === tag.value)) {
                draftTags.push(tag);
            }
        });
        
        setTags(newTags);
        setIsChanged(newTags.some(t => !t.saved));
    };

    const debouncedSearchTag = useDebounce(searchTag, 500);

    useEffect(() => {
        if (debouncedSearchTag && debouncedSearchTag.length > 1) {
            api.searchTags(debouncedSearchTag, 'cargo').then((results) => {
                setSuggestions(results);
            });
        } else {
            setSuggestions([]);
        }
    }, [debouncedSearchTag]);

    const saveTags = useCallback(async (cargoId, _tags) => {
        if (isSaving) {
            return false;
        }
        updateTagsDataInRow(cargoId, _tags);
        setIsSaving(true);

        try {
            const response = await api.saveTags(cargoId, _tags);

            const savedTags = immer.produce(_tags, draftTags => {
                draftTags.forEach(t => t.saved = true);
            });

            setIsChanged(false);
            setTags(savedTags);
            setPureTags(savedTags);

            addNotification({
                title: 'Successful saving',
                message: 'Tags saved!',
                level: 'success',
                autoDismiss: 3,
            });
        } catch (err) {
            const resp = err.response.data;
            addNotification({
                title: 'Error',
                message: resp.errors[0].message || resp.errors[0].messages[0],
                level: 'error',
                autoDismiss: 3,
            });
        }
        
        setIsSaving(false);
    }, [setIsSaving]);

    return (
        <div style={{ display: 'flex' }}>
            <div style={{ width: '70%' }}>
                <ReactTags
                    allowNew={false}
                    autofocus={false}
                    tags={tags}
                    suggestions={suggestions}
                    handleDelete={handleDelete}
                    handleAddition={handleAddition}
                    handleInputChange={val => setSearchTag(val)}
                    placeholder="Add Tag"
                    tagComponent={Tag}
                />
            </div>
            <div style={{
                width: '30%', textAlign: 'center', margin: 'auto', display: isChanged ? 'block' : 'none', 
            }}
            >
                <RaisedButton 
                    onClick={() => saveTags(cargoId, tags)}
                    labelStyle={{ padding: 0, margin: 0 }}
                    label='Save Tags'
                    primary
                />
            </div>
        </div>
    );
}

function Tag({ tag, removeButtonText, onDelete }) {
    return (
        <button type='button' title={removeButtonText} onClick={onDelete}>
            { tag.value }
            { tag.saved ? '' : '*' }
        </button>
    );
}

export default connect(
    null,
    dispatch => bindActionCreators(
        {
            addNotification: _addNotification,
            updateTagsDataInRow,
        },
        dispatch,
    ),
)(Tags);



// WEBPACK FOOTER //
// ./app/components/Cargoes/RealNames/Tags.jsx